import React from 'react';

interface FinalistFiltersProps {
  totalSubmissions: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  availablePillars: string[];
  filterPillar: string;
  setFilterPillar: (pillar: string) => void;
  allFilterCategories: string[];
  userCategory: string;
  filterCategory: string;
  setFilterCategory: (category: string) => void;
  filteredSubmissionsCount: number;
  submissionsCount: number;
}

const FinalistFilters: React.FC<FinalistFiltersProps> = ({
  totalSubmissions,
  searchQuery,
  setSearchQuery,
  availablePillars,
  filterPillar,
  setFilterPillar,
  allFilterCategories,
  userCategory,
  filterCategory,
  setFilterCategory,
  filteredSubmissionsCount,
  submissionsCount,
}) => {
  return (
    <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
      {/* Quick Status Info */}
      {totalSubmissions > 0 && (
        <div className="mb-4 rounded-lg p-3 bg-green-50 border border-green-200">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-sm font-baijamjuree font-medium text-green-800">
              🏆 Congratulations to all our finalists! Browse through their
              outstanding submissions below.
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
        {/* Search Input */}
        <div className="flex-1 lg:flex-[2]">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search submissions..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 bg-white font-baijamjuree text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Pillar Filter */}
        {availablePillars.length > 1 && (
          <div className="lg:flex-1">
            <select
              value={filterPillar}
              onChange={e => setFilterPillar(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-baijamjuree text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Pillars</option>
              {availablePillars.map(pillar => (
                <option key={pillar} value={pillar}>
                  {pillar === 'Others' ? 'Initiative' : pillar}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Category Filter - Show if judge has multiple categories OR if submissions have multiple categories */}
        {(allFilterCategories.length > 1 || userCategory.includes(',')) && (
          <div className="lg:flex-1">
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-baijamjuree text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Categories</option>
              {allFilterCategories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="mt-3 text-sm text-gray-600 font-baijamjuree">
        Showing {filteredSubmissionsCount} of {submissionsCount} finalists
        {filteredSubmissionsCount !== submissionsCount && (
          <span className="ml-2 text-blue-600">(filtered)</span>
        )}
      </div>
    </div>
  );
};

export default FinalistFilters;
